// Pure helpers shared by the coin list, the coin settings popup and the coin
// details popup — ported from the legacy Angular components
// (`v3-coin-settings-popup.component.ts`, `v3-coin-details-popup.component.ts`,
// `v3-coin-min-balance.component.*`, `v3-coin-list-spot.component.ts`,
// `asset-helper.ts`). The two popups carried byte-identical copies of
// `getConstantValue` / `getConstantLabel` / `getFieldLabel`; here they exist
// once. Their `getDefaultAutobuy*Label` twins are NOT identical in legacy
// (only the details popup falls back to the `*` entry and appends
// " - global (*)") — both variants are kept, named after their component.

import type { CoinConfig, ConstantEntry } from '../../api/tradingTypes'
import { PRIMARY_USD_ASSET, USD_ASSETS, isUsd } from './tradingConstants'

const SECONDARY_USD_ASSET = 'USDT'

/** Settings popup `tradingModes` (no Stopped). */
export const TRADING_MODES: Record<string, string> = {
  '-1': 'Inactive',
  '0': 'Off',
  '1': 'Buy and sell',
  '2': 'Sell only',
  '101': 'On (sets to Sell only)',
}
/** Details popup / coin list display labels (with Stopped). */
export const TRADING_MODES_WITH_STOPPED: Record<string, string> = { '-100': 'Stopped', ...TRADING_MODES }

/** Coin list bucket keys (`V3CoinListSpotComponent.tradingModes`) and the
 * fixed on-screen bucket order. */
export const TRADING_MODE_BUCKETS: Record<string, string> = {
  '-100': 'stopped',
  '-1': 'inactive',
  '0': 'off',
  '1': 'buyAndSell',
  '2': 'sellOnly',
}
export const BUCKET_ORDER = ['buyAndSell', 'sellOnly', 'off', 'inactive', 'stopped']

export const LOGIC_LABELS: Record<string, string> = { '0': 'No', '1': 'Yes' }
export const MAX_MOP_LABELS: Record<string, string> = { '0': 'Unlimited' }

/** Batch-modify `maxMopCounts` — verbatim (1..50, then steps of 5 to 100). */
export const MAX_MOP_COUNTS = [
  ...Array.from({ length: 50 }, (_, i) => i + 1),
  55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
]

/** `AssetHelper.formatNumberByCoin`: 2 decimals for USD / mirror-USD coins,
 * 8 otherwise, `,` thousands separator, trailing zero decimals stripped. */
export function formatNumberByCoin(value: number, coin?: string | null): string {
  const c = String(coin || PRIMARY_USD_ASSET).trim().toUpperCase()
  const mirrorUsd = USD_ASSETS.some((u) => `${u}*` === c)
  const precision = isUsd(c) || mirrorUsd ? 2 : 8
  const fixed = Number.isFinite(value) ? value.toFixed(precision) : '0'
  const [intPart, decPart = ''] = fixed.split('.')
  const withSep = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const trimmed = decPart.replace(/0+$/, '')
  return trimmed ? `${withSep}.${trimmed}` : withSep
}

/** `V3CoinMinBalanceComponent`: `45 / effective.gridUp * effective.bid`, or
 * `-` when either is falsy (a truthiness check — kept). */
export function minBalanceLabel(config: CoinConfig): string {
  const gridUp = Number(config.effective?.gridUp)
  const bid = Number(config.effective?.bid)
  if (!gridUp || !bid) return '-'
  return `${formatNumberByCoin((45 / gridUp) * bid, config.coin2)} ${config.coin2}`
}

function sentinelLabel(value: number | null): string | null {
  if (value === null || Number.isNaN(value)) return 'Not set'
  if (value === -2) return "Use trader's value"
  if (value === -1) return 'Use my default value'
  return null
}

/** `getConstantValue`: match `key`, or `key::USDC` / `key::USDT`. */
export function getConstantValue(name: string, constants: ConstantEntry[] | undefined | null): number | null {
  for (const constant of Array.isArray(constants) ? constants : []) {
    const key = String(constant.key)
    if (key === name || `${key}::${PRIMARY_USD_ASSET}` === name || `${key}::${SECONDARY_USD_ASSET}` === name) {
      return parseFloat(`${constant.value}`)
    }
  }
  return null
}

export function getConstantLabel(name: string, constants: ConstantEntry[] | undefined | null, postfix: string): string {
  const value = getConstantValue(name, constants)
  return sentinelLabel(value) ?? `${value}${postfix}`
}

export function getFieldLabel(value: unknown, postfix = '', valueMap?: Record<string, string>): string {
  if (value === null || value === undefined) return 'Not set'
  const n = Number(value)
  if (n === -2) return "Use trader's value"
  if (n === -1) return 'Use my default value'
  if (valueMap && Object.prototype.hasOwnProperty.call(valueMap, String(value))) return valueMap[String(value)] ?? ''
  return `${value}${postfix}`
}

export function getStringFieldLabel(value: string | null | undefined): string {
  if (value === null || value === undefined) return 'Not set'
  if (value === '') return 'Use my default value'
  return value
}

type AutoBuyDefault = { key: string | number; mode?: unknown; amount?: unknown; usdAmount?: unknown; asset?: string }

function defaultAutoBuy(config: CoinConfig): AutoBuyDefault[] {
  const list = config.default?.autoBuy
  return Array.isArray(list) ? (list as AutoBuyDefault[]) : []
}

// --- settings popup variants (exact coin2 match only, no `*` fallback) -----

export function settingsDefaultAutoBuyModeLabel(config: CoinConfig, modes: Record<string, string>): string {
  let value: unknown = null
  for (const c of defaultAutoBuy(config)) if (String(c.key) === config.coin2) value = c.mode
  return getFieldLabel(value, '', modes)
}

export function settingsDefaultAutoBuyAssetLabel(config: CoinConfig): string {
  let asset: string = config.coin2
  for (const c of defaultAutoBuy(config)) if (String(c.key) === config.coin2) asset = c.asset ?? ''
  return asset
}

export function settingsDefaultAutoBuyAmountLabel(config: CoinConfig): string {
  let value: unknown = null
  let asset = config.autoBuyAsset ?? ''
  for (const c of defaultAutoBuy(config)) {
    if (String(c.key) === config.coin2) {
      value = c.amount
      if (!asset) asset = c.asset ?? ''
      if (isUsd(asset)) value = c.usdAmount
    }
  }
  const sentinel = value === null || value === undefined ? 'Not set' : sentinelLabel(Number(value))
  return sentinel ?? `${value} ${asset}`
}

// --- details popup variants (`*` global fallback + " - global (*)") ------

function detailsLookup(config: CoinConfig, field: keyof AutoBuyDefault): { value: unknown; found: boolean } {
  let value: unknown = null
  let found = false
  for (const c of defaultAutoBuy(config)) {
    if (String(c.key) === config.coin2) {
      value = c[field]
      found = true
    }
  }
  if (!found) for (const c of defaultAutoBuy(config)) if (String(c.key) === '*') value = c[field]
  return { value, found }
}

function withPostfix(value: unknown, found: boolean, format: (v: unknown) => string): string {
  const postfix = found ? '' : ' - global (*)'
  if (value === null || value === undefined) return `Not set${postfix}`
  const n = Number(value)
  if (n === -2) return `Use trader's value${postfix}`
  if (n === -1) return `Use my default value${postfix}`
  return `${format(value)}${postfix}`
}

export function detailsDefaultAutoBuyModeLabel(config: CoinConfig, modes: Record<string, string>): string {
  const { value, found } = detailsLookup(config, 'mode')
  return withPostfix(value, found, (v) => modes[String(v)] ?? String(v))
}

export function detailsDefaultAutoBuyUsdAmountLabel(config: CoinConfig): string {
  const { value, found } = detailsLookup(config, 'usdAmount')
  return withPostfix(value, found, (v) => `${v} ${PRIMARY_USD_ASSET}`)
}

export function detailsDefaultAutoBuyAmountLabel(config: CoinConfig): string {
  const { value, found } = detailsLookup(config, 'amount')
  if (value === null || value === undefined || Number(value) === -2 || Number(value) === -1) {
    return withPostfix(value, found, String)
  }
  if (found) return `${value} ${config.coin2}`
  return detailsDefaultAutoBuyUsdAmountLabel(config)
}

export function detailsDefaultAutoBuyAssetLabel(config: CoinConfig): string {
  const { value, found } = detailsLookup(config, 'asset')
  const postfix = found ? '' : ' - global (*)'
  if (value === null || value === undefined) return `Not set${postfix}`
  if (value === '') return `Use my default value${postfix}`
  return `${value}${postfix}`
}

/** Row marker legacy painted `bg-medium-danger`: the exchange reports the
 * pair can not trade right now. */
export function isNotTrading(config: CoinConfig): boolean {
  const status = config.preferences?.status
  return Boolean(status) && status !== 'TRADING'
}

export function tradingModeLabel(config: CoinConfig): string {
  const mode = String(config.tradingMode ?? '')
  const base = TRADING_MODES_WITH_STOPPED[mode] ?? mode
  return isNotTrading(config) ? `${base} - non-tradeable (${config.preferences?.status})` : base
}

/** `sortCoinList` default order within a bucket: coin1 asc, then coin2 asc. */
export function compareCoinAsc(a: CoinConfig, b: CoinConfig): number {
  if (a.coin1 !== b.coin1) return a.coin1 < b.coin1 ? -1 : 1
  if (a.coin2 !== b.coin2) return a.coin2 < b.coin2 ? -1 : 1
  return 0
}

/** Legacy on-screen order: buckets `buyAndSell, sellOnly, off, inactive,
 * stopped`, coin asc inside each. */
export function orderByBucket(list: CoinConfig[]): CoinConfig[] {
  const rank = (c: CoinConfig) => {
    const bucket = TRADING_MODE_BUCKETS[String(Number(c.tradingMode ?? 0))] ?? 'off'
    return BUCKET_ORDER.indexOf(bucket)
  }
  return [...list].sort((a, b) => rank(a) - rank(b) || compareCoinAsc(a, b))
}
